import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Box,
  Card,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  AddShoppingCart,
  History,
  RemoveShoppingCart
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { Workflow } from '@sierralabs/workflow';

import { FetchError } from '../../../common/api/api';
import CardContent from '../../../common/card/CardContent';
import CollapsibleRow from '../../../common/CollapsibleRow';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { useTaskWorkflow } from '../../workflow/Workflow.hooks';
import TaskAuditLogList from '../components/TaskAuditLogList';
import { parseTaskStatus } from '../task.helper';
import { useGetVersionedTaskStatusLogs } from '../Task.hooks';
import { Task, TaskEntity, TaskStatusLog } from '../Task.interface';
import { getCartIndex, useCartContext } from '../../../common/cart/CartContext';

const StyledCard = styled(Card)`
  position: relative;
  .title {
    margin-top: 0;
    font-weight: 500;
  }
  .status-text {
    margin: 0;
    padding-top: 20px;
  }
  .task-buttons {
    position: absolute;
    top: 12px;
    right: 12px;
  }
`;

const StyledTableRow = styled(TableRow)`
  background-color: #f7f8fc;
`;

interface GroupedLog {
  version: number;
  statusLogs: TaskStatusLog[];
}

export type RenderContent = (
  group: GroupedLog,
  latestStatusLog: TaskStatusLog,
  isExpanded: boolean
) => React.ReactNode;

export interface TaskStatusVersionCardProps {
  showCart?: boolean;
  taskEntity: TaskEntity;
  renderContent?: RenderContent;
}

const TaskStatusVersionCard: React.FC<TaskStatusVersionCardProps> = (
  props: TaskStatusVersionCardProps
) => {
  const { showCart, taskEntity, renderContent } = props;
  const task = taskEntity.task!;
  const limit = 10;
  const pageRef = useRef(0);
  const { cart, addToCart, removeFromCart } = useCartContext();
  const [groupedLogs, setGroupedLogs] = useState<GroupedLog[]>([]);
  const [selected, setSelected] = useState<number | undefined>();
  const [showAuditLog, setShowAuditLog] = useState(false);
  const previousTaskRef = useRef(task);
  const {
    taskStatusLogList,
    isLoading: isLoadingLogs,
    loadingError,
    getStatusLogs
  } = useGetVersionedTaskStatusLogs(task.id || 0);
  const isLoading: boolean = isLoadingLogs;
  const error: FetchError | undefined = loadingError;

  const status: string = useMemo(() => {
    return parseTaskStatus(task.status) || 'N/A';
  }, [task]);

  const workflow: Workflow | undefined = useTaskWorkflow(task);

  // initial fetch data and when task prop changes
  useEffect(() => {
    if (previousTaskRef.current !== task) {
      // refetch from first page if task changes
      previousTaskRef.current = task;
      pageRef.current = 0;
    }
    getStatusLogs({ page: pageRef.current, limit });
  }, [task, getStatusLogs]);
  useEffect(() => {
    if (taskStatusLogList?.data) {
      setGroupedLogs(groupedLogs => {
        if (taskStatusLogList.page === 0) groupedLogs = [];
        const previousLogs = [...groupedLogs];
        return taskStatusLogList.data.reduce<GroupedLog[]>(
          (result, statusLog) => {
            if (groupedLogs.some(g => g.version === statusLog.version)) {
              // skip statusLog if version already exists (could be due to hot refresh)
              return result;
            }
            const previousGroup = result[result.length - 1];
            if (!previousGroup || previousGroup.version !== statusLog.version) {
              result.push({
                version: statusLog.version,
                statusLogs: [statusLog]
              });
            } else {
              previousGroup.statusLogs.push(statusLog);
            }
            return result;
          },
          previousLogs
        );
      });
    }
  }, [taskStatusLogList]);
  const isInCart = useMemo(() => {
    return getCartIndex(cart, taskEntity) > -1;
  }, [cart, taskEntity]);
  return (
    <StyledCard>
      <CardContent background='primary'>
        <Typography variant='overline' className='status-text'>
          Status
        </Typography>
        <Typography variant='h4' className='title'>
          {status}
        </Typography>
        <div className='task-buttons'>
          {showCart && (
            <Tooltip
              title={isInCart ? 'Remove from Cart' : 'Add to Cart'}
              placement='top'
              arrow
            >
              <IconButton
                className='cart-button'
                size='small'
                color='secondary'
                onClick={() => {
                  if (isInCart) {
                    removeFromCart(taskEntity);
                  } else {
                    addToCart(taskEntity);
                  }
                }}
              >
                {isInCart ? <RemoveShoppingCart /> : <AddShoppingCart />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={showAuditLog ? 'Status Log' : 'Audit Log'}
            placement='top'
            arrow
          >
            <IconButton
              className='audit-log'
              size='small'
              color='secondary'
              onClick={() => setShowAuditLog(!showAuditLog)}
            >
              <History />
            </IconButton>
          </Tooltip>
        </div>
      </CardContent>
      {showAuditLog ? (
        <TaskAuditLogList task={task} />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={1} />
                <TableCell>Ver.</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>{'Date and Time'}</TableCell>
                <TableCell>Owner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={5} padding='none'>
                    <Alert severity='error'>{error?.message}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {groupedLogs?.map(group => {
                const isSelected: boolean = selected === group.version;
                return (
                  <TaskStatusVersionCardRow
                    key={group.version}
                    task={task}
                    workflow={workflow}
                    group={group}
                    isSelected={isSelected}
                    renderContent={renderContent}
                    onIconClick={() => {
                      setSelected(!isSelected ? group.version : undefined);
                    }}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {(!taskStatusLogList ||
        taskStatusLogList.total > (pageRef.current + 1) * limit) && (
        <Box textAlign='center' padding={2}>
          <Link
            variant='body2'
            component='button'
            onClick={() => {
              pageRef.current++;
              getStatusLogs({ page: pageRef.current, limit });
            }}
          >
            Load More
          </Link>
        </Box>
      )}
    </StyledCard>
  );
};

export interface TaskStatusVersionCardRowProps {
  task: Task;
  group: GroupedLog;
  workflow?: Workflow;
  isSelected?: boolean;
  onIconClick?: () => void;
  renderContent?: RenderContent;
}

const TaskStatusVersionCardRow: React.FC<TaskStatusVersionCardRowProps> = (
  props: TaskStatusVersionCardRowProps
) => {
  const { task, group, isSelected = false, onIconClick, renderContent } = props;
  // const { isFinal } = useTaskStateType(undefined, statusLog.status, workflow);

  const latestStatusLog = group.statusLogs[0];
  const collapsedContent: React.ReactNode | undefined = useMemo(() => {
    return (
      <>
        {renderContent?.(group, latestStatusLog, isSelected)}
        <Table>
          <TableBody>
            {/* Display sub versions */}
            {group.statusLogs.map(statusLog => {
              if (statusLog === latestStatusLog) return null;
              return (
                <StyledTableRow key={statusLog.id}>
                  <TableCell />
                  <TableCell width={60}>{statusLog.version}</TableCell>
                  <TableCell>
                    <TaskStatusLabel task={task} status={statusLog.status} />
                  </TableCell>
                  <TableCell>{formatDate(statusLog.created)}</TableCell>
                  <TableCell>
                    {statusLog.createdBy.firstName}{' '}
                    {statusLog.createdBy.lastName}
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }, [group, latestStatusLog, task, isSelected, renderContent]);
  return (
    <>
      <CollapsibleRow
        colSpan={5}
        open={isSelected}
        // hideAction={!isFinal}
        collapsedContent={collapsedContent}
        onIconClick={onIconClick}
      >
        <TableCell width={60}>{group.version}</TableCell>
        <TableCell>
          <TaskStatusLabel task={task} status={latestStatusLog.status} />
        </TableCell>
        <TableCell>{formatDate(latestStatusLog.created)}</TableCell>
        <TableCell>
          {latestStatusLog.createdBy.firstName}{' '}
          {latestStatusLog.createdBy.lastName}
        </TableCell>
      </CollapsibleRow>
    </>
  );
};

export default TaskStatusVersionCard;
